import * as Sentry from '@sentry/vue';

import {
  registerRuntimeObservabilityHandlers,
  type ObservabilityContext,
  type ObservabilityTags,
} from '@peanuds/shared/observability/runtime-observability';

type SentryObservabilityIdentity = {
  userId: string | null;
  organizationId?: string | null;
  ifExternalUserId?: string | null;
  ifClientId?: string | null;
  sessionStatus?: string | null;
  isTokenExpired?: boolean | null;
};

type SentryObservabilityOptions = {
  getIdentity?: () => SentryObservabilityIdentity;
};

const toSentryRecord = (values: ObservabilityTags | ObservabilityContext | undefined) => {
  if (values === undefined) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== null && value !== undefined),
  );
};

const applyIdentity = (scope: Sentry.Scope, identity: SentryObservabilityIdentity | null) => {
  if (identity === null) {
    scope.setUser(null);
    return undefined;
  }

  scope.setUser(identity.userId === null ? null : { id: identity.userId });

  return toSentryRecord({
    organizationId: identity.organizationId ?? null,
    ifExternalUserId: identity.ifExternalUserId ?? null,
    ifClientId: identity.ifClientId ?? null,
    sessionStatus: identity.sessionStatus ?? null,
    isTokenExpired: identity.isTokenExpired ?? null,
  });
};

const resolveIdentity = (options?: SentryObservabilityOptions) => {
  try {
    return options?.getIdentity?.() ?? null;
  } catch {
    return null;
  }
};

export const registerSentryObservability = (options?: SentryObservabilityOptions) => {
  registerRuntimeObservabilityHandlers({
    captureEvent: (event) => {
      Sentry.withScope((scope) => {
        const identityContext = applyIdentity(scope, resolveIdentity(options));
        Sentry.captureMessage(event.name, {
          level: event.level,
          tags: toSentryRecord(event.tags),
          contexts: {
            ...(event.context === undefined
              ? {}
              : {
                  details: toSentryRecord(event.context),
                }),
            ...(identityContext === undefined
              ? {}
              : {
                  identity: identityContext,
                }),
          },
        });
      });
    },
    addBreadcrumb: (breadcrumb) => {
      Sentry.addBreadcrumb({
        category: breadcrumb.category,
        message: breadcrumb.message,
        level: breadcrumb.level,
        data: toSentryRecord(breadcrumb.data),
      });
    },
  });
};
